import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import Card from '../../components/base/Card'
import Input from '../../components/base/Input'
import Button from '../../components/base/Button'
import TurnstileGate from '../../components/TurnstileGate'
import type { TurnstileGateHandle } from '../../components/TurnstileGate'

export default function ForgotPasswordPage() {
  const gateRef = useRef<TurnstileGateHandle>(null)
  const [token, setToken] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('') // 폼/API 에러 공용
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // 클라이언트 입력 검증
    if (!email.trim()) {
      setError('이메일을 입력해주세요')
      return
    }

    if (!validateEmail(email)) {
      setError('올바른 이메일 형식이 아닙니다')
      return
    }

    if (!token) {
      setError('봇 검증을 완료해 주세요.')
      gateRef.current?.show() // (선택) 혹시 숨겨져 있으면 보여주기
      return
    }

    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/reset-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, turnstileToken: token })
      })

      if (!res.ok) {
        let msg = '비밀번호 재설정 요청 중 오류가 발생했습니다.'
        gateRef.current?.reset() // ✅ 다시 체크 요구
        try {
          const data = await res.json()
          if (data?.error) msg = data.error
        } catch {
          // JSON 파싱 실패 시 기본 메시지 사용
        }
        setError(msg)
        return
      }

      // 성공 시: "이메일을 확인하세요" 화면으로 전환
      setSubmitted(true)
    } catch (err) {
      console.error(err)
      setError('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) setError('')
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-check-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">이메일을 확인하세요</h2>
            <p className="text-gray-600 mb-6">
              <strong>{email}</strong>로 비밀번호 재설정 링크를 보내드렸어요.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              이메일을 받지 못하셨다면 스팸 폴더를 확인하거나, 잠시 후 다시 시도해주세요.
            </p>
            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => setSubmitted(false)}
              >
                이메일 다시 보내기
              </Button>
              <Link to="/login">
                <Button variant="ghost" size="lg" className="w-full">
                  <i className="ri-arrow-left-line mr-2"></i>
                  로그인으로 돌아가기
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-password-line text-3xl text-blue-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">비밀번호를 잊으셨나요?</h1>
          <p className="text-gray-600">
            걱정하지 마세요. 비밀번호 재설정 안내를 이메일로 보내드릴게요.
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="이메일"
              name="email"
              type="email"
              placeholder="이메일을 입력하세요"
              icon="ri-mail-line"
              value={email}
              onChange={handleChange}
              error={error}
            />

            <TurnstileGate ref={gateRef} action="forgot" onTokenChange={setToken} />

            {error && <p className="text-sm text-red-600">{error}</p>}

            <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
              재설정 링크 보내기
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              로그인으로 돌아가기
            </Link>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            계정이 없으신가요?{' '}
            <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium">
              회원가입
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
